import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { type CssVarsTheme, useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { usePaperWidth } from './hooks';
import RegularDialogButton from './RegularDialogButton';
import RegularDialogTitle from './RegularDialogTitle';

export interface RegularDialogProps extends Omit<DialogProps, 'title'> {
  title?: string | React.ReactElement<any>;
  titleIcon?: React.ReactNode;
  titleAction?: React.ReactNode;
  button?: React.ReactNode;
  hideButton?: boolean;
  actions?: React.ReactNode;
}

export const RegularDialog = ({
  title,
  titleIcon,
  titleAction,
  button,
  open,
  children,
  maxWidth,
  hideButton,
  actions,
  onClose,
  ...props
}: RegularDialogProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const paperWidth = usePaperWidth(maxWidth);
  const theme = useTheme() as CssVarsTheme;
  const descriptionElementRef = useRef<HTMLElement>(null);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
    setIsOpen(false);
  };

  useEffect(() => {
    setIsOpen(open);
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  return (
    <>
      {button || (
        <RegularDialogButton hideButton={hideButton} onClick={handleOpen} />
      )}

      <Dialog
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        data-testid="dialog"
        onClose={handleClose}
        scroll="paper"
        slotProps={{
          backdrop: {
            style: {
              backgroundColor: (theme.vars || theme).palette.background
                .backdrop,
            },
          },
          paper: paperWidth,
        }}
        open={isOpen}
        {...props}
      >
        <RegularDialogTitle
          titleIcon={titleIcon}
          titleAction={titleAction}
          title={title}
          closeAction={handleClose}
          id="dialog-title"
        />
        <DialogContent
          dividers
          data-testid="dialog-content"
          ref={descriptionElementRef}
          tabIndex={-1}
          id="dialog-description"
          sx={{ pt: 1.5, ...(actions && { borderBottom: 0 }) }}
        >
          {children}
        </DialogContent>
        {actions && (
          <DialogActions
            data-testid="dialog-actions"
            sx={{ px: 3, py: 2, width: '100%' }}
          >
            {actions}
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
